/**
 * GoodiesWidget Component
 *
 * Displays a grid of helpful resources and tools for the selected brand.
 * Features responsive layout, smooth animations, and polished visual design.
 * Used in the Dashboard sidebar to provide quick access to brand analysis and brand kit.
 */

import { Link } from "react-router-dom";
import { Sparkles, BarChart3, Palette, ArrowRight, Zap } from "lucide-react";
import { useBrands } from '@/lib/BrandsProvider';
import { Card, CardContent } from "@/components/ui/card";

interface GoodiesWidgetProps {
  className?: string;
}

export function GoodiesWidget({ className = "" }: GoodiesWidgetProps) {
  const { selectedBrand } = useBrands();

  // Don't render if no brand is selected
  if (!selectedBrand) {
    return null;
  }

  const goodies = [
    {
      id: 'brand-analysis',
      title: 'Brand Analysis',
      description: 'Dive deep into your brand insights and performance metrics',
      icon: BarChart3,
      link: `/brands/${selectedBrand.id}/analysis`,
      color: 'from-purple-500 to-indigo-600',
      hoverColor: 'hover:from-purple-600 hover:to-indigo-700',
      emoji: '📊',
      subtitle: 'Get the data story'
    },
    {
      id: 'brand-kit',
      title: 'Brand Kit',
      description: 'Access your complete brand guidelines and visual assets',
      icon: Palette,
      link: '/brand/guide',
      color: 'from-pink-500 to-rose-600',
      hoverColor: 'hover:from-pink-600 hover:to-rose-700',
      emoji: '🎨',
      subtitle: 'Style & consistency'
    }
  ];

  return (
    <Card className={`shadow-sm overflow-hidden ${className}`}>
      <CardContent className="p-0">
        {/* Responsive Grid: Stack on mobile, side-by-side on larger screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-gray-700">
          {goodies.map((goodie) => {
            const Icon = goodie.icon;

            return (
              <Link
                key={goodie.id}
                to={goodie.link}
                className="group relative overflow-hidden transition-all duration-300 ease-out hover:bg-gray-50/50 dark:hover:bg-gray-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                aria-label={`Explore ${goodie.title}: ${goodie.description}`}
              >
                <div className="p-5 sm:p-6 min-h-[200px] sm:min-h-[220px] flex flex-col">
                  {/* Background gradient that appears on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${goodie.color} opacity-0 group-hover:opacity-[0.03] dark:group-hover:opacity-[0.08] transition-opacity duration-500 ease-out`} />

                  {/* Content */}
                  <div className="relative z-10 flex-1 flex flex-col">
                    {/* Header with icon and emoji */}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br ${goodie.color} rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transform group-hover:rotate-6 group-hover:scale-110 transition-all duration-300 ease-out`}>
                        <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <div className="text-2xl sm:text-3xl opacity-40 group-hover:opacity-60 transition-opacity duration-300 leading-none">
                        {goodie.emoji}
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1.5 leading-tight group-hover:text-gray-800 dark:group-hover:text-gray-50 transition-colors duration-200">
                      {goodie.title}
                    </h3>

                    {/* Subtitle */}
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider leading-tight">
                      {goodie.subtitle}
                    </p>

                    {/* Description */}
                    <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300 mb-5 flex-1 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors duration-200">
                      {goodie.description}
                    </p>

                    {/* Action button and arrow */}
                    <div className="flex items-center justify-between mt-auto">
                      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${goodie.color} text-white text-sm font-medium transform group-hover:scale-105 group-hover:shadow-lg transition-all duration-300 ease-out shadow-md`}>
                        <Zap className="h-4 w-4" />
                        <span>Explore</span>
                      </span>
                      <ArrowRight className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transform group-hover:translate-x-1.5 transition-all duration-300 ease-out" />
                    </div>
                  </div>

                  {/* Decorative sparkles */}
                  <div className="absolute top-3 right-3 opacity-20 group-hover:opacity-40 transition-opacity duration-500">
                    <div className="relative">
                      <Sparkles className="h-4 w-4 text-yellow-400 animate-pulse" />
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full opacity-75 animate-ping" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default GoodiesWidget;